import React, { useMemo } from "react"
import spinner from "../../../assets/spinner.svg"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"

type SpinnerSize = "xs" | "sm" | "md" | "lg"

interface SpinnerProps {
  size?: SpinnerSize
  text?: string
  parentStyleOverrides?: string
  textStyleOverrides?: string
  containerStyleOverrides?: string
}

const Spinner: React.FC<SpinnerProps> = ({
  size = "md",
  text = "Loading",
  parentStyleOverrides,
  textStyleOverrides,
  containerStyleOverrides,
}) => {
  const variantSizes = {
    xs: "w-8 h-8",
    sm: "w-12 h-12",
    md: "",
    lg: "w-32 h-32",
  }
  const variants = cva(["w-32", "h-24", "overflow-hidden", "rounded-md"], {
    variants: { size: variantSizes },
  })

  const parentContainerClassName = useMemo(() => {
    const defaultStyle = [
      "h-screen",
      "flex",
      "flex-col",
      "items-center",
      "justify-center",
    ]
    return cn(defaultStyle, parentStyleOverrides)
  }, [parentStyleOverrides])

  const spinnerContainerClassName = useMemo(() => {
    const defaultStyle = ["animate-spin"]
    return cn(variants({ size }), defaultStyle, containerStyleOverrides)
  }, [variants, size, containerStyleOverrides])

  const textContainerClassName = useMemo(() => {
    const defaultStyle = ["py-2", "text-base", "text-gray-500"]
    return cn(defaultStyle, textStyleOverrides)
  }, [textStyleOverrides])

  return (
    <div className={parentContainerClassName}>
      <div className={variantSizes[size]}>
        <img
          src={spinner}
          className={spinnerContainerClassName}
          alt={`${text} spinner`}
        />
      </div>
      {text && (
        <div className={textContainerClassName} aria-hidden={!text}>
          {text}
        </div>
      )}
    </div>
  )
}

export default Spinner
